import React, { useEffect, useState } from "react";
import "./GunList.css";
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePlus, AiOutlineQrcode, AiOutlineSearch } from "react-icons/ai";
import { getAllGuns, deleteGun } from "../../services/gunService";
import { getProducts } from "../../services/productService";
import GunModal from "./GunRegistrationModal";
import Popup from "../../components/ui/Popup";
import { QRCodeCanvas } from "qrcode.react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle, AiOutlineTool, AiOutlineDatabase } from "react-icons/ai";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";

function GunList() {
    const [guns, setGuns] = useState([]);
    const [products, setProducts] = useState([]);
    const [isGunModalOpen, setIsGunModalOpen] = useState(false);
    const [selectedGun, setSelectedGun] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [qrModal, setQrModal] = useState(null);
    const [popupMessage, setPopupMessage] = useState("");
    const [filters, setFilters] = useState({
        serialNumber: "",
        status: "",
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        fetchGuns();
        fetchProducts();
    }, []);

    useEffect(() => {
        // Reset to first page when filters change
        setCurrentPage(1);
    }, [filters]);

    const fetchGuns = async () => {
        try {
            const data = await getAllGuns();
            // console.log("Fetched guns:", data);
            setGuns(data);
        } catch (error) {
            console.error("Failed to fetch guns:", error);
        }
    };

    const fetchProducts = async () => {
        try {
            const data = await getProducts();
            console.log("Fetched products:", data);
            setProducts(data);
        } catch (error) {
            console.error("Failed to fetch gun models:", error);
        }
    };

    const totalGuns = guns.length;
    const inStock = guns.filter(g => g.status === "In Stock").length;
    const soldGuns = guns.filter(g => g.status === "Sold").length;
    const repairing = guns.filter(g => g.status === "Under Repair").length;

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
    };

    const filteredGuns = guns.filter((gun) => {
        // Serial number filter
        const serialNumberMatch =
            !filters.serialNumber ||
            (gun.serial_number?.toString().toLowerCase() || '')
                .includes(filters.serialNumber.toLowerCase());

        // Status filter
        const statusMatch =
            !filters.status ||
            filters.status === "" ||
            gun.status === filters.status;

        return serialNumberMatch && statusMatch;
    });

    const printQRCode = (gun, size) => {
        // Prepare QR code data with comprehensive information

        const qrData = JSON.stringify({
            serial_number: gun.serial_number,
            product_name: gun.product_name,
            product_id: gun.product_id
        });
        console.log(qrData);
        // Print sizes for thermal printers
        const printSizes = {
            tiny: { width: 58, height: 40, qrSize: 40 },
            small: { width: 80, height: 45, qrSize: 55 },
            medium: { width: 90, height: 60, qrSize: 65 }
        };

        const selectedSize = printSizes[size] || printSizes.small;

        // Create a data URI for the QR code
        const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${selectedSize.qrSize * 5}x${selectedSize.qrSize * 5}&data=${encodeURIComponent(qrData)}`;

        // Create print window with absolute minimal content
        const printWindow = window.open('', '_blank');

        // Write minimal HTML structure
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>QR Code</title>
            <style>
                @page {
                    size: ${selectedSize.width}mm ${selectedSize.height}mm;
                    margin: 0;
                }
                
                html, body {
                    margin: 0;
                    padding: 0;
                    height: 100%;
                    overflow: hidden;
                    background-color: white;
                    /* Critical fix - prevent pagination */
                    page-break-inside: avoid;
                    page-break-after: avoid;
                }
                
                .print-container {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                
                .qr-only {
                    display: block;
                }
                
                /* Force QR image to stay contained on one page */
                @media print {
                    html, body {
                        height: ${selectedSize.height}mm;
                        width: ${selectedSize.width}mm;
                    }
                    
                    .print-container {
                        height: ${selectedSize.height}mm;
                        width: ${selectedSize.width}mm;
                        position: absolute;
                    }
                }
            </style>
        </head>
        <body>
            <div class="print-container">
                <img 
                    class="qr-only" 
                    src="${qrUrl}" 
                    alt="QR Code"
                    width="${selectedSize.qrSize}mm"
                    height="${selectedSize.qrSize}mm"
                />
            </div>
            <script>
                // Fetch the image into cache before printing
                const img = new Image();
                img.onload = function() {
                    // Replace the current image element with the loaded one
                    document.querySelector('.qr-only').src = img.src;
                    
                    // Print after a short delay
                    setTimeout(function() {
                        // Critical: remove any element that might cause pagination
                        document.querySelectorAll('*').forEach(el => {
                            el.style.pageBreakInside = 'avoid';
                            el.style.pageBreakAfter = 'avoid';
                            el.style.pageBreakBefore = 'avoid';
                            // Modern CSS properties
                            el.style.breakInside = 'avoid';
                            el.style.breakAfter = 'avoid';
                            el.style.breakBefore = 'avoid';
                        });
                        
                        // Force flush layout before printing
                        document.body.offsetHeight;
                        
                        // Now print
                        window.print();
                        
                        // Close window after print
                        window.onafterprint = function() {
                            window.close();
                        };
                    }, 200);
                };
                img.src = "${qrUrl}";
                
                // Fallback in case image load event doesn't fire
                setTimeout(function() {
                    if (!document.hasPrinted) {
                        document.hasPrinted = true;
                        window.print();
                        window.onafterprint = function() {
                            window.close();
                        };
                    }
                }, 1000);
            </script>
        </body>
        </html>
    `);

        printWindow.document.close();
    };

    // Pagination calculations
    const totalPages = Math.ceil(filteredGuns.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredGuns.slice(indexOfFirstItem, indexOfLastItem);

    // Page change handlers
    const goToPage = (pageNumber) => {
        setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
    };

    const goToPreviousPage = () => {
        setCurrentPage(prevPage => Math.max(1, prevPage - 1));
    };

    const goToNextPage = () => {
        setCurrentPage(prevPage => Math.min(totalPages, prevPage + 1));
    };

    // Generate page number buttons
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // If fewer than maxVisiblePages, show all pages
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always include first and last pages
            // And make sure current page is always visible with some neighbors

            // Start with page 1
            pageNumbers.push(1);

            // Calculate start and end of middle section
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            // Adjust if we're close to the beginning
            if (currentPage <= 3) {
                endPage = Math.min(totalPages - 1, 4);
            }

            // Adjust if we're close to the end
            if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 3);
            }

            // Add ellipsis after page 1 if needed
            if (startPage > 2) {
                pageNumbers.push('...');
            }

            // Add middle pages
            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
            }

            // Add ellipsis before last page if needed
            if (endPage < totalPages - 1) {
                pageNumbers.push('...');
            }

            // End with last page
            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    return (
        <div className="content-wrapper">
            <div className="statistics">
                <div className="stat-tile in-stock">
                    <AiOutlineCheckCircle /> In Stock: {inStock}
                </div>
                <div className="stat-tile sold">
                    <AiOutlineCloseCircle /> Sold: {soldGuns}
                </div>
                <div className="stat-tile repair">
                    <AiOutlineTool /> Under Repair: {repairing}
                </div>
                <div className="stat-tile total">
                    <AiOutlineDatabase /> Total Guns: {totalGuns}
                </div>
            </div>

            <div className="filters">
                <input
                    type="text"
                    name="serialNumber"
                    placeholder="Search Serial Number..."
                    value={filters.serialNumber}
                    onChange={handleFilterChange}
                />

                <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                >
                    <option value="">All Status</option>
                    <option value="In Stock">In Stock</option>
                    <option value="Sold">Sold</option>
                    <option value="Under Repair">Under Repair</option>
                </select>

                <button className="search-btn">
                    <AiOutlineSearch /> Search
                </button>

                <button className="add-gun-btn" onClick={() => setIsGunModalOpen(true)}>
                    <AiOutlinePlus /> Register Gun
                </button>
            </div>

            <div className="table-wrapper">
                <table>
                    <thead>
                    <tr>
                        <th>Serial Number</th>
                        <th>Product Name</th>
                        <th>Status</th>
                        <th>QR Code</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentItems.map((gun) => (
                        <tr key={gun.id}>
                            <td>{gun.serial_number}</td>
                            <td>{gun.product_name}</td>
                            <td className="status-cell">{gun.status}</td>
                            <td>
                                <AiOutlineQrcode
                                    className="qr-icon"
                                    onClick={() => setQrModal(gun)}
                                />
                            </td>
                            <td className="actions">
                                <button
                                    className="edit-btn"
                                    onClick={() => {
                                        setSelectedGun(gun);
                                        setIsGunModalOpen(true);
                                    }}
                                >
                                    <AiOutlineEdit className="edit-icon" />
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 0 && (
                <div className="pagination">
                    <button
                        className="pagination-btn"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                    >
                        <AiOutlineLeft /> Prev
                    </button>

                    <div className="pagination-pages">
                        {getPageNumbers().map((page, index) => (
                            page === '...' ? (
                                <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                            ) : (
                                <button
                                    key={page}
                                    className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                    onClick={() => goToPage(page)}
                                >
                                    {page}
                                </button>
                            )
                        ))}
                    </div>

                    <button
                        className="pagination-btn"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                    >
                        Next <AiOutlineRight />
                    </button>
                </div>
            )}

            <div className="pagination-info">
                {filteredGuns.length > 0 ? (
                    <span>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredGuns.length)} of {filteredGuns.length} entries</span>
                ) : (
                    <span>No records found</span>
                )}
            </div>

            {qrModal && (
                <div className="qr-modal">
                    <div className="qr-modal-content">
                        <div className="qr-modal-header">
                            <h2>QR Code Details</h2>
                            <button
                                className="qr-modal-close"
                                onClick={() => setQrModal(null)}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="qr-modal-body">
                            <div className="qr-code-display">
                                <QRCodeCanvas
                                    value={JSON.stringify({
                                        serial_number: qrModal.serial_number,
                                        product_name: qrModal.product_name,
                                        product_id: qrModal.product_id
                                    })}
                                    size={300}
                                    level="H"
                                    includeMargin={true}
                                />
                            </div>

                            <div className="qr-details">
                                <h3>Gun Details</h3>
                                <p><strong>Serial Number:</strong> {qrModal.serial_number}</p>
                                <p><strong>Product:</strong> {qrModal.product_name}</p>
                                <p><strong>Product ID:</strong> {qrModal.product_id}</p>
                            </div>
                        </div>

                        <div className="qr-print-options">
                            <h4>Print Label Size:</h4>
                            <div className="print-size-buttons">
                                <button
                                    onClick={() => printQRCode(qrModal, 'tiny')}
                                    className="print-size-btn"
                                >
                                    Small (58mm)
                                </button>
                                <button
                                    onClick={() => printQRCode(qrModal, 'small')}
                                    className="print-size-btn"
                                >
                                    Standard (80mm)
                                </button>
                                <button
                                    onClick={() => printQRCode(qrModal, 'medium')}
                                    className="print-size-btn"
                                >
                                    Large (90mm)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isGunModalOpen && (
                <GunModal
                    isOpen={isGunModalOpen}
                    gun={selectedGun}
                    products={products}
                    onClose={() => {
                        setIsGunModalOpen(false);
                        setSelectedGun(null);
                    }}
                    onGunUpdated={fetchGuns}
                    refreshGunList={fetchGuns} // Pass fetchGuns as a prop to refresh list
                />
            )}

            {popupMessage && <Popup message={popupMessage} onClose={() => setPopupMessage("")} />}
        </div>
    );

}

export default GunList;